import React from "react";
import EndpointCard from "../EndpointCard";
import CodeBlock from "../CodeBlock";

const SOPGeneration = () => {
  const BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:8000/api/v1";

  return (
    <section className="section">
      <h2 className="section-title">SOP Generation Tasks</h2>
      <p className="text-gray-300 mb-6 leading-7 max-w-[65ch]">
        Monitor and track SOP generation tasks. Use the global endpoint to poll
        task status without needing the project UUID. Tasks are created via the
        Projects endpoint and generate SOPs asynchronously using a multi-agent
        pipeline.
      </p>

      <EndpointCard
        method="POST"
        path="/projects/{project_uuid}/sopGenerationTasks"
        title="Create SOP Generation Task"
        description="Asynchronously starts the multi-agent pipeline to generate a new SOP. Returns a task UUID to poll for completion. The generated SOP will be associated with this project."
        tags={["Authentication: Required", "Async: Yes"]}
        parameters={{
          path: {
            project_uuid: {
              required: true,
              description: "The unique identifier of the project",
            },
          },
        }}
        requestBody={{
          source_namespace_uuid: "{namespace_uuid}",
          prompt: "Create an SOP for handling customer support escalations",
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Request Body:
            </h4>
            <CodeBlock
              code={`{
  "source_namespace_uuid": "{namespace_uuid}",
  "prompt": "Create an SOP for handling customer support escalations"
}`}
            />
            <p className="text-xs text-gray-400 mt-2">
              <strong>source_namespace_uuid</strong> (required): UUID of the
              namespace containing documents to use for SOP generation
              <br />
              <strong>prompt</strong> (required): The prompt describing what SOP
              to generate
            </p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              cURL Example:
            </h4>
            <CodeBlock
              code={`curl -X POST "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "source_namespace_uuid": "{namespace_uuid}",
    "prompt": "Create an SOP for handling customer support escalations"
  }'`}
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Response (202 Accepted):
            </h4>
            <CodeBlock
              code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "PENDING",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": null,
  "output_sop_uuid": null
}`}
            />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>Note:</strong> SOP generation is asynchronous and can take
              several minutes. Use the returned
              <code className="bg-blue-900/50 px-1 py-0.5 rounded">
                {" "}
                uuid{" "}
              </code>{" "}
              (task UUID) to poll the task status endpoint. Once completed, the{" "}
              <code className="bg-blue-900/50 px-1 py-0.5 rounded">
                {" "}
                output_sop_uuid{" "}
              </code>{" "}
              will contain the generated SOP UUID.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="GET"
        path="/sopGenerationTasks/{task_uuid}"
        title="Get SOP Generation Task"
        description="Retrieves the status of a SOP generation task by its UUID. This is a global endpoint that doesn't require the project UUID."
        tags={["Authentication: Required"]}
        parameters={{
          path: {
            task_uuid: {
              required: true,
              description: "The unique identifier of the SOP generation task",
            },
          },
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Path Parameters:
            </h4>
            <table className="parameter-table">
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <code>task_uuid</code>
                  </td>
                  <td>string (UUID)</td>
                  <td>
                    <span className="required-badge">Required</span>
                  </td>
                  <td>The unique identifier of the SOP generation task</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              cURL Example:
            </h4>
            <CodeBlock
              code={`curl -X GET "${BASE_URL}/sopGenerationTasks/{task_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`}
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Response (200 OK):
            </h4>
            <CodeBlock
              code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "COMPLETED",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": "{...}",
  "output_sop_uuid": "{sop_uuid}"
}`}
            />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>Note:</strong> For more detailed progress information, use
              the project-specific endpoint:
              <code className="bg-blue-900/50 px-1 py-0.5 rounded">
                GET /projects/{"{project_uuid}"}/sopGenerationTasks/
                {"{task_uuid}"}
              </code>
              which includes structured progress data with stages and
              percentages.
            </p>
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="PATCH"
        path="/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/cancel"
        title="Cancel SOP Generation Task"
        description="Cancels a SOP generation task if it's still running. Only PENDING or PROCESSING tasks can be cancelled."
        tags={["Authentication: Required"]}
        parameters={{
          path: {
            project_uuid: {
              required: true,
              description: "The unique identifier of the project",
            },
            task_uuid: {
              required: true,
              description:
                "The unique identifier of the SOP generation task to cancel",
            },
          },
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              cURL Example:
            </h4>
            <CodeBlock
              code={`curl -X PATCH "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/cancel" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`}
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Response (200 OK):
            </h4>
            <CodeBlock
              code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "CANCELLED",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": null,
  "output_sop_uuid": null
}`}
            />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="PATCH"
        path="/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/pause"
        title="Pause SOP Generation Task"
        description="Pauses a running SOP generation task. The task can be resumed later using the resume endpoint."
        tags={["Authentication: Required"]}
        parameters={{
          path: {
            project_uuid: {
              required: true,
              description: "The unique identifier of the project",
            },
            task_uuid: {
              required: true,
              description:
                "The unique identifier of the SOP generation task to pause",
            },
          },
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              cURL Example:
            </h4>
            <CodeBlock
              code={`curl -X PATCH "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/pause" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`}
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Response (200 OK):
            </h4>
            <CodeBlock
              code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "PAUSED",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": null,
  "output_sop_uuid": null
}`}
            />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="PATCH"
        path="/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/resume"
        title="Resume SOP Generation Task"
        description="Resumes a paused SOP generation task. The task will continue from where it was paused."
        tags={["Authentication: Required", "Async: Yes"]}
        parameters={{
          path: {
            project_uuid: {
              required: true,
              description: "The unique identifier of the project",
            },
            task_uuid: {
              required: true,
              description:
                "The unique identifier of the SOP generation task to resume",
            },
          },
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              cURL Example:
            </h4>
            <CodeBlock
              code={`curl -X PATCH "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/resume" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`}
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Response (200 OK):
            </h4>
            <CodeBlock
              code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "source_namespace_uuid": "{namespace_uuid}",
  "status": "PROCESSING",
  "prompt": "Create an SOP for handling customer support escalations",
  "result": null,
  "output_sop_uuid": null
}`}
            />
          </div>
        </div>
      </EndpointCard>

      <EndpointCard
        method="POST"
        path="/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/submit-answers"
        title="Submit Clarifying Question Answers"
        description="Submit answers to clarifying questions when SOP generation is blocked waiting for user input. The generation will automatically resume with your answers as context."
        tags={["Authentication: Required", "Async: Yes"]}
        parameters={{
          path: {
            project_uuid: {
              required: true,
              description: "The unique identifier of the project",
            },
            task_uuid: {
              required: true,
              description: "The unique identifier of the SOP generation task",
            },
          },
        }}
        requestBody={{
          answers: {
            "What are the prerequisites?":
              "All tests must pass and code review completed",
            "Who are the stakeholders?":
              "Product Manager, Tech Lead, DevOps Engineer",
          },
        }}
      >
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Understanding BLOCKED_USER_INPUT Status:
            </h4>
            <div className="p-4 bg-orange-900/20 border border-orange-700/50 rounded-lg">
              <p className="text-sm text-orange-200 mb-3">
                When the SOP generation pipeline needs clarification, the task
                status changes to{" "}
                <code className="bg-orange-900/50 px-1 py-0.5 rounded">
                  BLOCKED_USER_INPUT
                </code>{" "}
                and the response includes:
              </p>
              <ul className="text-sm text-orange-200 space-y-1 list-disc list-inside">
                <li>
                  <code className="bg-orange-900/50 px-1 py-0.5 rounded">
                    interview_questions
                  </code>
                  : Array of questions to answer
                </li>
                <li>
                  <code className="bg-orange-900/50 px-1 py-0.5 rounded">
                    interaction_required
                  </code>
                  : true
                </li>
                <li>
                  <code className="bg-orange-900/50 px-1 py-0.5 rounded">
                    interaction_type
                  </code>
                  : "clarifying_questions"
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Step 1: Check Task Status
            </h4>
            <CodeBlock
              code={`curl -X GET "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid}" \\
  -H "X-API-Key: email@example.com:your_api_key_here"`}
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Response when blocked (200 OK):
            </h4>
            <CodeBlock
              code={`{
  "uuid": "{task_uuid}",
  "status": "BLOCKED_USER_INPUT",
  "interview_questions": [
    "What are the exact prerequisites and approvals required?",
    "Who are the key stakeholders involved in each phase?",
    "What criteria determine a successful deployment?"
  ],
  "interaction_required": true,
  "interaction_type": "clarifying_questions",
  "interaction_data": {
    "message": "Please answer the clarifying questions to continue workflow generation."
  }
}`}
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Step 2: Submit Answers
            </h4>
            <CodeBlock
              code={`curl -X POST "${BASE_URL}/projects/{project_uuid}/sopGenerationTasks/{task_uuid}/submit-answers" \\
  -H "X-API-Key: email@example.com:your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "answers": {
      "What are the exact prerequisites and approvals required?": "All unit tests pass, code review approved by 2 engineers, staging verified for 24 hours",
      "Who are the key stakeholders involved in each phase?": "Product Manager (approval), Tech Lead (code review), DevOps (deployment)",
      "What criteria determine a successful deployment?": "Health endpoint returns 200, error rate < 0.1%, all smoke tests pass"
    }
  }'`}
            />
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-300 mb-2">
              Response (200 OK):
            </h4>
            <CodeBlock
              code={`{
  "uuid": "{task_uuid}",
  "project_uuid": "{project_uuid}",
  "status": "PROCESSING",
  "prompt": "Create an SOP for...",
  "interview_answers": {...}
}`}
            />
          </div>

          <div className="p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
            <p className="text-sm text-blue-200">
              <strong>Note:</strong> After submitting answers, the SOP
              generation resumes automatically in the background. The task
              status changes to{" "}
              <code className="bg-blue-900/50 px-1 py-0.5 rounded">
                PROCESSING
              </code>{" "}
              and continues using your answers as context. Poll the task status
              endpoint to track progress.
            </p>
          </div>
        </div>
      </EndpointCard>
    </section>
  );
};

export default SOPGeneration;
